import { useQuery, useMutation } from "@apollo/client";
import { faStamp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTeam } from "@hooks";
import { Tooltip } from "@mui/material";
import mixpanel from "mixpanel-browser";
import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";

import TemplateSelectContext from "../../../../context/templateSelectContext.js";
import { RETRY_FAILED_AUTO_SOAP } from "../../../../graphql/mutations/RetryFailedAutoSoap.js";
import { GET_AUTO_TEMPLATES } from "../../../../graphql/queries/AutoTemplates.js";
import { useNoteUsage } from "../../../../hooks/use-note-usage.js";
import AutoTemplatesModal from "../../../autotemplates/AutoTemplatesModal";
import { alert } from "../../../common/Alert.js";

function UseTemplateButtonLegacy({ note, dataTestId }) {
  const {
    setShouldShowTemplateSelectModal,
    setUuidOfNoteToOverwriteWithTemplate,
  } = useContext(TemplateSelectContext);

  const noteUUID = note?.uuid || note?.note?.uuid;

  return (
    <>
      <Tooltip
        arrow
        title={
          "Click to select a template to apply. A template will overwrite any existing content in the note."
        }
      >
        <span>
          <button
            id="useTemplate"
            data-testid={dataTestId}
            onClick={() => {
              setShouldShowTemplateSelectModal(true);
              alert(
                "warning",
                "Warning: selecting a template will overwrite any existing text in this note's fields.",
              );
              if (noteUUID) {
                setUuidOfNoteToOverwriteWithTemplate(noteUUID);
              }
            }}
            className="rounded-full font-semibold px-3 h-[40px] flex flex-row items-center bg-indigo-500 disabled:dark:text-gray-500 text-white hover:bg-indigo-600 transition-all focus:outline-none border border-indigo-500 disabled:bg-gray-300 disabled:dark:bg-gray-700 disabled:dark:border-gray-700 disabled:hover:bg-gray-300 disabled:border-gray-300 disabled:cursor-not-allowed shadow-md"
          >
            <FontAwesomeIcon icon={faStamp} className="md:mr-2" />
            <span className="hidden md:block whitespace-nowrap">
              Use Template
            </span>
          </button>
        </span>
      </Tooltip>
    </>
  );
}

export function UseTemplateButtonNew({ note, dataTestId }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { currentTeam } = useTeam();

  const { data, refetch } = useQuery(GET_AUTO_TEMPLATES, {
    variables: {
      teamUuid: currentTeam?.uuid,
    },
  });
  const templates = data?.autoTemplates || [];

  const [retryFailedAutoSoap] = useMutation(RETRY_FAILED_AUTO_SOAP);

  const navigate = useNavigate();

  const handleTemplateSelect = async (template) => {
    try {
      await retryFailedAutoSoap({
        variables: {
          noteUuid: note?.uuid || note?.note?.uuid,
          jobType: template.value || "template",
          templateUuid: template.uuid,
        },
      });
      // Same pattern as current reprocessing
      // WEB-301 addresses potential improvements
      alert(
        "success",
        "Template applied successfully! Your note is now reprocessing.",
      );
      navigate(`/dashboard`);
      mixpanel.track("Reprocessed note as template.", {
        noteUuid: note?.uuid || note?.note?.uuid,
        templateUuid: template.uuid,
        jobType: note?.jobType || note?.note?.jobType,
      });
    } catch (error) {
      alert("error", "Failed to apply template. Please try again.");
      mixpanel.track("Failed to apply template", {
        noteUuid: note?.uuid || note?.note?.uuid,
        templateUuid: template.uuid,
        jobType: note?.jobType || note?.note?.jobType,
        error: error.message,
      });
    }

    setIsModalOpen(false);
  };

  return (
    <>
      <AutoTemplatesModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onTemplateSelect={handleTemplateSelect}
        templates={templates}
        refetch={refetch}
        isReprocessingMode={true}
      />
      <span>
        <button
          data-testid={dataTestId}
          onClick={() => {
            setIsModalOpen(true);
          }}
          className="rounded-full font-semibold px-3 h-[40px] flex flex-row items-center bg-indigo-500 disabled:dark:text-gray-500 text-white hover:bg-indigo-600 transition-all focus:outline-none border border-indigo-500 disabled:bg-gray-300 disabled:dark:bg-gray-700 disabled:dark:border-gray-700 disabled:hover:bg-gray-300 disabled:border-gray-300 disabled:cursor-not-allowed shadow-md"
        >
          <FontAwesomeIcon icon={faStamp} className="md:mr-2" />
          <span className="hidden md:block whitespace-nowrap">
            Use Template
          </span>
        </button>
      </span>
    </>
  );
}

export function UseTemplateButton({ note }) {
  const { shouldUseAutoSoaps } = useNoteUsage();

  if (shouldUseAutoSoaps) {
    return (
      <UseTemplateButtonNew
        note={note}
        dataTestId="new-use-template-button"
      />
    );
  }

  return (
    <UseTemplateButtonLegacy
      note={note}
      dataTestId="legacy-use-template-button"
    />
  );
}
